import React from 'react';
import { addMonths } from 'date-fns';
import { useTheme, useMediaQuery, Box, styled } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DatePickerWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})(({ theme, fullWidth }) => ({
  width: fullWidth ? '100%' : 'auto',
  display: 'block',
  '& .react-datepicker-wrapper': { display: 'block', width: '100%' },
  '& .react-datepicker__input-container': { width: '100%', position: 'relative' },
  '& .react-datepicker__input-container input': {
    width: '100%',
    boxSizing: 'border-box',
    height: 36,
    paddingLeft: 8,
    minWidth: 192,
    border: '1px solid',
    borderColor: theme.palette.neutral.main,
    borderRadius: theme.shape.borderRadius,
    fontSize: 14,
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    '&:focus': {
      outline: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}33`,
    },
  },
  '& .react-datepicker__calendar-icon': {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    fill: theme.palette.text.primary,
    cursor: 'pointer',
  },
  '& .react-datepicker-popper': { zIndex: 999 },
  '& .react-datepicker__month-container': { display: 'inline-block' },
  '& .react-datepicker': {
    display: 'flex',
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
    boxShadow: theme.shadows[4],
  },
}));

const formatDate = (date) =>
  date instanceof Date && !isNaN(date)
    ? date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '';

// Controlled date range picker — no context, no Redux.
// Props: startDate (Date|null), endDate (Date|null), onChange([start, end])
const DateRangeField = ({ startDate, endDate, onChange, fullWidth = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatValue = () => {
    if (!startDate && !endDate) return 'Select date range';
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  return (
    <DatePickerWrapper fullWidth={fullWidth}>
      <DatePicker
        selected={startDate}
        startDate={startDate}
        endDate={endDate}
        minDate={endDate ? addMonths(endDate, -12) : null}
        maxDate={startDate ? addMonths(startDate, 12) : null}
        selectsRange
        monthsShown={isMobile ? 1 : 2}
        showIcon
        toggleCalendarOnIconClick
        dateFormat="dd/MM/yyyy"
        placeholderText="Select date range"
        onChange={onChange}
        value={formatValue()}
        popperPlacement="bottom-start"
        popperProps={{
          positionFixed: true,
          modifiers: [{ name: 'preventOverflow', options: { padding: 8 } }],
        }}
      />
    </DatePickerWrapper>
  );
};

export default DateRangeField;
